/**
 * usePatchClinicAdmin — PATCH /v1/admin/clinics/{clinic_id}
 *
 * Tenant-admin route. Updates `mips_active`, `mips_activated_year`,
 * `apu_id`, `reporting_type`, `submission_method`, `clinic_alias`. The
 * response echoes the updated row.
 *
 * Powers the admin clinic-activation page (inline toggles + edits). Also
 * called from the dashboard's Practice Card settings modal for the AM-
 * editable fields (apu_id, reporting_type, submission_method).
 *
 * Invalidates the dashboard (for the practice card fields) and the clinic
 * list (for the activation page).
 */
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { getApiClient } from "@/api/client";
import type { ClinicActivationRequest, ClinicActivationResponse } from "@/api/models";
import { queryKeys } from "./queryKeys";

export interface PatchClinicAdminVariables {
  clinicId: number;
  body: ClinicActivationRequest;
}

export function usePatchClinicAdmin() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({
      clinicId,
      body,
    }: PatchClinicAdminVariables): Promise<ClinicActivationResponse> => {
      const { data } = await getApiClient().patch<ClinicActivationResponse>(
        `/v1/admin/clinics/${clinicId}`,
        body,
      );
      return data;
    },
    onSuccess: (_data, vars) => {
      void qc.invalidateQueries({ queryKey: queryKeys.dashboard.byClinic(vars.clinicId) });
      void qc.invalidateQueries({ queryKey: queryKeys.clinics.all });
      // V1.2.0: admin list shows full clinic shape (incl. inactive,
      // eligible_clinician_count, specialty_set_codes) — refresh on patch.
      void qc.invalidateQueries({ queryKey: queryKeys.adminClinics.all });
    },
  });
}
